import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const PaymentContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  padding-top: 100px;
`;

const PaymentHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const HeaderTitle = styled(motion.h1)`
  font-family: "Playfair Display", serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: #ffffff;
  margin-bottom: 1rem;

  span {
    color: #ffd700;
  }
`;

const HeaderSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: #b0b0b0;
  max-width: 600px;
  margin: 0 auto;
`;

const PricingSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 60px;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled(motion.div).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !["featured"].includes(prop) && defaultValidatorFn(prop),
})`
  background: ${(props) =>
    props.featured
      ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"};
  padding: 3rem;
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  border: ${(props) =>
    props.featured ? "none" : "1px solid rgba(255, 215, 0, 0.2)"};
  transform: ${(props) => (props.featured ? "scale(1.05)" : "scale(1)")};

  ${(props) =>
    props.featured &&
    `
    &::before {
      content: 'MOST POPULAR';
      position: absolute;
      top: 20px;
      right: -30px;
      background: #0a0a0a;
      color: #FFD700;
      padding: 5px 40px;
      transform: rotate(45deg);
      font-size: 0.8rem;
      font-weight: 700;
    }
  `}
`;

const PlanName = styled.h3`
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  color: ${(props) => (props.featured ? "#0a0a0a" : "#FFD700")};
  margin-bottom: 1rem;
`;

const PlanPrice = styled.div`
  font-size: 3.5rem;
  font-weight: 700;
  color: ${(props) => (props.featured ? "#0a0a0a" : "#ffffff")};
  margin-bottom: 0.5rem;

  span {
    font-size: 1.5rem;
    font-weight: 400;
  }
`;

const PlanDuration = styled.div`
  color: ${(props) => (props.featured ? "#0a0a0a" : "#b0b0b0")};
  margin-bottom: 2rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 2rem;
`;

const Feature = styled.li`
  color: ${(props) => (props.featured ? "#0a0a0a" : "#ffffff")};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "✓";
    color: ${(props) => (props.featured ? "#0a0a0a" : "#FFD700")};
    font-weight: 700;
  }
`;

const SelectButton = styled(motion.button)`
  width: 100%;
  padding: 1rem 2rem;
  background: ${(props) => (props.featured ? "#0a0a0a" : "#FFD700")};
  color: ${(props) => (props.featured ? "#FFD700" : "#0a0a0a")};
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.3);
  }
`;

const PaymentForm = styled(motion.div)`
  max-width: 600px;
  margin: 0 auto;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  padding: 3rem;
  border-radius: 30px;
  border: 1px solid rgba(255, 215, 0, 0.2);
`;

const FormTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  color: #ffd700;
  margin-bottom: 2rem;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #b0b0b0;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 10px;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
  color: #b0b0b0;
  font-size: 0.9rem;

  &::before {
    content: "🔒";
    font-size: 1.2rem;
  }
`;

const PayButton = styled(motion.button)`
  width: 100%;
  padding: 1.2rem 2rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffa500 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 40px rgba(255, 215, 0, 0.4);
  }
`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const plans = [
    {
      id: "basic",
      name: "Basic",
      price: "$49",
      duration: "per month",
      featured: false,
      features: [
        "20 lessons per month",
        "AI pronunciation feedback",
        "Basic vocabulary builder",
        "Email support",
      ],
    },
    {
      id: "premium",
      name: "Premium",
      price: "$99",
      duration: "per month",
      featured: true,
      features: [
        "Unlimited lessons",
        "Personal AI tutor",
        "Live conversation practice",
        "Priority support",
        "Certificate of completion",
        "Accent reduction tools",
      ],
    },
    {
      id: "vip",
      name: "VIP Elite",
      price: "$299",
      duration: "per month",
      featured: false,
      features: [
        "Everything in Premium",
        "1-on-1 native speaker sessions",
        "Custom learning path",
        "Business English specialization",
        "VIP concierge support",
        "Lifetime access to materials",
      ],
    },
  ];

  const handlePlanSelect = (planId) => {
    const planExists = plans.some((plan) => plan.id === planId);
    if (planExists) {
      setSelectedPlan(planId);
      setShowPaymentForm(true);
    } else {
      alert("Selected plan does not exist.");
    }
  };

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePayment = (e) => {
    e.preventDefault();
    // Payment processing logic would go here
    alert("Payment successful! Welcome to Ferman Premium!");
    navigate("/dashboard");
  };

  return (
    <PaymentContainer>
      <PaymentHeader>
        <HeaderTitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Choose Your <span>Learning Journey</span>
        </HeaderTitle>
        <HeaderSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Invest in yourself with our premium language learning plans
        </HeaderSubtitle>
      </PaymentHeader>

      {!showPaymentForm ? (
        <PricingSection>
          <PricingGrid>
            {plans.map((plan, index) => (
              <PricingCard
                key={plan.id}
                featured={plan.featured}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                style={{
                  boxShadow:
                    selectedPlan === plan.id
                      ? "0 0 0 4px #FFD700, 0 10px 40px rgba(255,215,0,0.15)"
                      : undefined,
                }}
              >
                <PlanName featured={plan.featured}>{plan.name}</PlanName>
                <PlanPrice featured={plan.featured}>
                  {plan.price}
                  <span>/mo</span>
                </PlanPrice>
                <PlanDuration featured={plan.featured}>
                  {plan.duration}
                </PlanDuration>
                <FeatureList>
                  {plan.features.map((feature, idx) => (
                    <Feature key={idx} featured={plan.featured}>
                      {feature}
                    </Feature>
                  ))}
                </FeatureList>
                <SelectButton
                  featured={plan.featured}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  {plan.featured ? "Get Premium" : "Select Plan"}
                </SelectButton>
              </PricingCard>
            ))}
          </PricingGrid>
        </PricingSection>
      ) : (
        <PaymentForm
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <FormTitle>Enter Payment Details</FormTitle>
          <form onSubmit={handlePayment}>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                placeholder="John Doe"
                required
                value={formData.fullName}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                name="email"
                placeholder="john@example.com"
                required
                value={formData.email}
                onChange={handleInputChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Card Number</Label>
              <Input
                type="text"
                name="cardNumber"
                placeholder="1234 5678 9012 3456"
                required
                value={formData.cardNumber}
                onChange={handleInputChange}
              />
            </FormGroup>
            <CardRow>
              <FormGroup>
                <Label>Expiry Date</Label>
                <Input
                  type="text"
                  name="expiry"
                  placeholder="MM/YY"
                  required
                  value={formData.expiry}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>CVV</Label>
                <Input
                  type="text"
                  name="cvv"
                  placeholder="123"
                  required
                  value={formData.cvv}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </CardRow>
            <SecurityBadge>
              Your payment information is secure and encrypted
            </SecurityBadge>
            <PayButton
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Pay Now & Start Learning
            </PayButton>
          </form>
        </PaymentForm>
      )}
    </PaymentContainer>
  );
};

export default PaymentPage;
